import {
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  useProjectSecurityContext,
  RISK_UPDATE,
  RISK_DELETE,
  RISK_ESCALATE,
  RISK_DEESCALATE,
} from 'domain/shared/newProject';
import { useAsyncValue } from 'framework/utils';
import { useAuthorization } from 'framework/authorization';
import useAuthenticatedUserService from 'infrastructure/security/authentication/authenticatedUserService';

export interface RiskFieldPermissions {
  canEditRestrictedFields: boolean;
  canEditCustomerReportFlag: boolean;
}

interface RiskPermissions extends RiskFieldPermissions {
  canEdit: boolean;
  canDelete: boolean;
  canEscalate: boolean;
  canDeescalate: boolean;
}

export const ALL_RISK_FIELDS_EDITABLE: RiskFieldPermissions = Object.freeze({
  canEditRestrictedFields: true,
  canEditCustomerReportFlag: true,
});

interface UseRiskPermissionsParams {
  createdBy?: string;
  riskOwner?: string;
}

const useRiskPermissions = ({ createdBy, riskOwner }: UseRiskPermissionsParams): RiskPermissions => {
  const { isAccessGranted } = useProjectSecurityContext();
  const { isGranted } = useAuthorization();
  const { getUserAlias } = useAuthenticatedUserService();
  const currentUserAlias = useAsyncValue(() => getUserAlias());

  const hasManage = isAccessGranted(ACCESS_INFO_SECTIONS.RISK, ACCESS_INFO_ACTIONS.MANAGE);
  const hasPartialManage = isAccessGranted(ACCESS_INFO_SECTIONS.RISK, ACCESS_INFO_ACTIONS.PARTIAL_MANAGE);
  const isAuthor = Boolean(createdBy) && createdBy === currentUserAlias;
  const isOwner = Boolean(riskOwner) && riskOwner === currentUserAlias;

  const canEditRestrictedFields = hasManage || (hasPartialManage && isAuthor);

  return {
    canEditRestrictedFields,
    canEditCustomerReportFlag: hasManage,
    canEdit: isGranted(RISK_UPDATE) && (canEditRestrictedFields || (hasPartialManage && isOwner)),
    canDelete: isGranted(RISK_DELETE),
    canEscalate: isGranted(RISK_ESCALATE) || isAuthor || isOwner,
    canDeescalate: isGranted(RISK_DEESCALATE) || isAuthor || isOwner,
  };
};

export default useRiskPermissions;
